import {
  JxtAlert,
  JxtAlertTypeEnum,
  JxtExceptionalOpeningHoursDetails,
  JxtLoader,
  JxtOffCanvasHeaderHideIconEnum,
} from '@jooxter/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOffCanvasStore } from '../../store';
import { IRange, OffCanvasModeEnum, createGTMGAEvent, fromISO, isSameTimezoneAsTheUser, now } from '@jooxter/utils';
import { useFetchExceptionalOpeningHours, useFetchResource } from '../../queries';
import { useTimezoneNameFormatter } from '../useTimezoneNameFormatter';
import { JxtExceptionalOpeningHoursFormContainer } from '../../components';
import { useExceptionalOpeningHoursToIJxtExceptionalOpeningHoursDetailsAdapter } from '../../adapters/exceptional-opening-hours/useExceptionalOpeningHoursToIJxtExceptionalOpeningHoursDetailsAdapter';
import { useExceptionalOpeningHoursActions } from './useExceptionalOpeningHoursActions';
import { capitalize } from 'lodash-es';
import { IOffCanvasContent } from '../offCanvasContent.types';

// Reads the resource slice of the off-canvas store and turns it into the props JxtOffCanvas needs -
// the resource counterpart of useBookingOffCanvasContent, mounted once at the off-canvas host.
export const useResourceOffCanvasContent = (): IOffCanvasContent => {
  const { format: formatTimezoneName } = useTimezoneNameFormatter();
  const { mode, resourceId, exceptionalOpeningHoursId, preset } = useOffCanvasStore((state) => state.resource);
  const { close, resourceGoToEditView, setResourceMode } = useOffCanvasStore((state) => ({
    close: state.close,
    resourceGoToEditView: state.resourceGoToEditView,
    setResourceMode: state.setResourceMode,
  }));
  const { t } = useTranslation();
  const { exceptionalOpeningHours } = useFetchExceptionalOpeningHours(exceptionalOpeningHoursId);
  const { resource } = useFetchResource(resourceId ?? exceptionalOpeningHours?.resourceId);
  const { exceptionalOpeningHoursDetails } =
    useExceptionalOpeningHoursToIJxtExceptionalOpeningHoursDetailsAdapter(exceptionalOpeningHours);
  const bookingRange: IRange | undefined = useMemo(() => {
    if (
      exceptionalOpeningHours?.opened &&
      exceptionalOpeningHours.timestampTo &&
      exceptionalOpeningHours.timestampFrom &&
      fromISO(exceptionalOpeningHours.timestampTo) > now()
    ) {
      return { from: fromISO(exceptionalOpeningHours.timestampFrom), to: fromISO(exceptionalOpeningHours.timestampTo) };
    }
    return undefined;
  }, [exceptionalOpeningHours]);
  const { cancel, deleteEventModal } = useExceptionalOpeningHoursActions({
    resourceId,
    range: bookingRange,
    exceptionalOpeningHours,
    exceptionalOpeningHoursDetails,
  });
  const resourceTimezone = resource?.location?.timezone;
  const shouldShowTimezoneWarning = resourceTimezone ? !isSameTimezoneAsTheUser(resourceTimezone) : false;

  const resetAndCloseOffCanvas = () => {
    if (mode) {
      createGTMGAEvent('ManageSpaces', capitalize(mode), 'Close Button');
    }
    close();
  };

  const getIconList = (): { iconName: string; title: string; onClick: () => void }[] => {
    const icons: { iconName: string; title: string; onClick: () => void }[] = [];

    if (OffCanvasModeEnum.View !== mode || !exceptionalOpeningHours) {
      return icons;
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (fromISO(exceptionalOpeningHours.timestampFrom!).plus({ minutes: 5 }) > now()) {
      icons.push({
        iconName: 'pen',
        title: t<string>('update'),
        onClick: () => {
          resourceGoToEditView();
          createGTMGAEvent('ManageSpaces', 'Update', 'Modify Button');
        },
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (fromISO(exceptionalOpeningHours.timestampTo!) > now()) {
      icons.push({
        iconName: 'trash',
        title: t<string>('delete'),
        onClick: () => {
          cancel();
          createGTMGAEvent('ManageSpaces', 'Update', 'Delete Button');
        },
      });
    }

    return icons;
  };

  return useMemo(
    () => ({
      header: {
        hideIcon:
          mode === OffCanvasModeEnum.Update ? JxtOffCanvasHeaderHideIconEnum.Back : JxtOffCanvasHeaderHideIconEnum.Close,
        iconList: getIconList(),
        title:
          mode !== OffCanvasModeEnum.View ? (
            <header className="max-w-full text-center">
              <h3 className="text-neutral-140 font-medium truncate">{t('manage-space.offcanvas.header.title')}</h3>
            </header>
          ) : null,
        children: mode !== OffCanvasModeEnum.View && (
          <>
            {shouldShowTimezoneWarning && resourceTimezone && (
              <JxtAlert text={formatTimezoneName(resourceTimezone)} type={JxtAlertTypeEnum.Warning} closeable={false} />
            )}
          </>
        ),
      },
      onHide: () => {
        if (mode === OffCanvasModeEnum.Update) {
          setResourceMode(OffCanvasModeEnum.View);
        } else {
          resetAndCloseOffCanvas();
        }
      },
      bodyClass: 'overflow-y-auto',
      children: (
        <>
          {mode === OffCanvasModeEnum.View && !!exceptionalOpeningHoursDetails && (
            <JxtExceptionalOpeningHoursDetails {...exceptionalOpeningHoursDetails} />
          )}
          {mode === OffCanvasModeEnum.Create && (
            <JxtExceptionalOpeningHoursFormContainer exceptionalOpeningHoursFormPreset={{ ...preset, resource }} />
          )}
          {mode === OffCanvasModeEnum.Update && (
            <JxtExceptionalOpeningHoursFormContainer exceptionalOpeningHoursToEdit={exceptionalOpeningHours} />
          )}
          {!(exceptionalOpeningHours || resource) && (
            <div className="absolute top-0 right-0 left-0 bottom-0 backdrop-blur-sm h-full flex flex-1 items-center justify-center">
              <JxtLoader />
            </div>
          )}
          {deleteEventModal}
        </>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mode,
      deleteEventModal,
      resource,
      preset,
      exceptionalOpeningHours,
      exceptionalOpeningHoursDetails,
      shouldShowTimezoneWarning,
      formatTimezoneName,
    ]
  );
};
